<template>
    <div class="main">
        <FormWrapper :action="formAction" :method="formMethod">
            <div class="title-bar">
                <h1 class="mb-0">{{ order.id ? "Edit" : "Add New" }} Order</h1>
                <a class="btn-back" :href="route('admin.orders.index')">
                    <span class="icon icon-arrow-left"></span>
                    <span class="text">Back to List</span>
                </a>
            </div>
            <div class="row no-gutters py-4">
                <div class="col-12 col-lg-8 col-xl-9">
                    <div class="row no-gutters pl-4">
                        <div class="col-12">
                            <transition name="fade">
                                <Alert v-if="hasAlert" :alert="alert"/>
                            </transition>
                        </div>
                        <div class="col-12 mb-3 col-md-7 pr-md-4">
                            <div class="row">
                                <div class="col-12 mb-4">
                                    <div class="row">
                                        <div class="col-12 mb-3 col-xl-6">
                                            <Field
                                                v-if="isDraft"
                                                type="select"
                                                label="Course"
                                                name="course_id"
                                                :options="courses"
                                                v-model="selectedCourse"
                                                validate="required"
                                                validate-as="course"
                                                :hideSelectEmptyOption="true"
                                            />
                                            <Field
                                                v-else
                                                type="text"
                                                name="course"
                                                label="Course"
                                                :value="order.event.course.name"
                                                :disabled="true"
                                            />
                                        </div>
                                        <div class="col-12 mb-3 col-xl-6">
                                            <Field
                                                v-if="isDraft"
                                                type="select"
                                                label="Event"
                                                name="event_id"
                                                :options="eventOptions"
                                                v-model="selectedEvent"
                                                validate="required"
                                                validate-as="event"
                                                :hideSelectEmptyOption="true"
                                            />
                                            <Field
                                                v-else
                                                type="text"
                                                name="event"
                                                label="Event"
                                                :value="eventDisplayName(order.event)"
                                                :disabled="true"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <template v-if="selectedEvent || order.id">
                                    <div class="col-12">
                                        <MaterialsList :materials="materials" :is-editable="isDraft || isBuilt" />
                                        <hr>
                                    </div>
                                    <div class="col-12 col-md-4 mb-3">
                                        <Field
                                            type="datepicker"
                                            name="will_ship_at"
                                            label="Estimated Ship Date"
                                            :value="order.will_ship_at"
                                        />
                                    </div>
                                    <div class="col-12 mb-3">
                                        <Field
                                            type="textarea"
                                            name="notes"
                                            label="Notes"
                                            :value="order.notes"
                                        />
                                    </div>
                                    <div
                                        v-if="order.id && !isDraft"
                                        class="col-12 col-md-4 mb-3"
                                    >
                                        <Field
                                            type="text"
                                            name="order_number"
                                            label="Order Number"
                                            :value="order.order_number"
                                        />
                                    </div>

                                    <template v-if="order.id && !isDraft && !isBuilt">
                                        <div class="col-12 col-md-4 mb-3">
                                            <Field
                                                type="select"
                                                name="shipment_carrier"
                                                label="Shipment Carrier"
                                                v-model="shipmentCarrier"
                                                :options="shipmentCarriers"
                                                :hide-select-empty-option="true"
                                            />
                                        </div>
                                        <div class="col-12 mb-3">
                                            <Field
                                                type="text"
                                                name="tracking_number"
                                                label="Tracking Number(s)"
                                                :value="order.tracking_number"
                                                help-text="Enter multiple tracking numbers separated by comma"
                                            />
                                        </div>
                                        <div class="col-12 mb-3" v-if="shipmentCarrier == 'OTHER'">
                                            <Field
                                                type="text"
                                                name="tracking_url"
                                                label="Tracking URL"
                                                :value="order.tracking_url"
                                            />
                                        </div>
                                        <div class="col-12 col-md-4 mb-3">
                                            <Field
                                                type="datepicker"
                                                name="shipped_at"
                                                label="Actual Date Shipped"
                                                :value="order.shipped_at"
                                            />
                                        </div>
                                    </template>
                                    <div
                                        v-if="isShipped || hasArrived"
                                        class="col-12 mb-3"
                                    >
                                        <div class="row">
                                            <div class="col-12 col-md-4">
                                                <Field
                                                    type="datepicker"
                                                    name="arrived_at"
                                                    label="Arrival Date"
                                                    :value="order.arrived_at"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </div>
                        <div class="col-12 mb-4 col-md-6 col-xl-5">
                            <EventDetails v-if="event" :event="event" />
                        </div>
                    </div>
                </div>
                <div class="col-12 col-lg-4 col-xl-3 form-button-wrapper">
                    <div class="sticky-top">
                        <ActionButtons v-if="!isCancelled" :order="order" />

                        <div v-if="order.id" class="font-size-sm text-center pt-4">
                            <span>Last updated on {{ order.updated_at | moment("MM/DD/YYYY") }}</span>
                            <span>at {{ order.updated_at | moment("h:mma") }}</span>
                            <span v-if="order.updated_by">{{ " " + order.updated_by.name }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </FormWrapper>
    </div>
</template>

<script>
import DisplaysAlerts from "mixins/DisplaysAlerts";
import MaterialsList from './partials/MaterialsList'
import EventOverview from '../../../partials/Events/EventOverview'
import EventDetails from './partials/EventDetails'
import ActionButtons from './partials/ActionButtons'

export default {
    components: {ActionButtons, EventDetails, EventOverview, MaterialsList},
    mixins: [DisplaysAlerts],
    props: {
        order: {type: Object, required: true},
        courses: {type: Array, required: true},
        shipmentCarriers: {type: Array, required: true},
    },
    data() {
        return {
            selectedCourse: null,
            selectedEvent: null,
            shipmentCarrier: null,
        };
    },
    computed: {
        formAction() {
            return ! this.order.id
                ? this.route("admin.orders.store")
                : this.route("admin.orders.update", {
                    order: this.order.id,
                });
        },
        formMethod() {
            return ! this.order.id ? "POST" : "PUT";
        },

        isDraft() {
            return (
                ! this.order.status ||
                this.order.status == this.$root.constants.DRAFT_ORDER
            );
        },

        isBuilt() {
            return this.order.status === this.$root.constants.BUILT_ORDER;
        },

        isPlaced() {
            return this.order.status === this.$root.constants.PLACED_ORDER;
        },

        isShipped() {
            return this.order.status === this.$root.constants.SHIPPED_ORDER;
        },

        hasArrived() {
            return this.order.status === this.$root.constants.ARRIVED_ORDER;
        },

        isCancelled() {
            return this.order.status === this.$root.constants.CANCELLED_ORDER;
        },

        eventOptions() {
            let options = [];
            if (!! this.selectedCourse) {
                this.courses.forEach((course) => {
                    if (course.value == this.selectedCourse && !! course.events) {
                        course.events.forEach((event) => {
                            options.push({label: this.eventDisplayName(event), value: event.id});
                        });
                    }
                });
                if (options.length === 0) {
                    options.push({label: "No upcoming events available", value: null});
                } else {
                    options.unshift({label: "Select an event", value: null});
                }
            }
            return options;
        },
        materials() {
            const materials = this.order && this.order.materials && this.order.materials.length
                ? this.order.materials
                : this.selectedCourseMaterials;

            return materials.map(item => {
                return {
                    ...item,
                    num_ordered: item.pivot.num_ordered || 0
                };
            });
        },

        selectedCourseMaterials() {
            return this.selectedCourseInstance.materials.filter(material => material.is_active);
        },

        selectedCourseInstance() {
            return this.courses.find(course => course.value === this.selectedCourse)
        },

        selectedEventInstance() {
            if (!this.selectedCourseInstance) {
                return;
            }

            return this.selectedCourseInstance.events.find(event => event.id === this.selectedEvent)
        },

        event() {
            return this.order && this.order.event
                ? this.order.event
                : this.selectedEventInstance
        }
    },

    created() {
        if (this.isDraft && this.order.id) {
            this.selectedEvent = this.order.event_id || null;
            this.selectedCourse = this.order.event.course_id || null;
        }

        if (this.order.id) {
            this.shipmentCarrier = this.order.shipment_carrier || 'FEDEX';
        }
    },

    methods: {
        eventDisplayName(event) {
            let label = event.id + ": ";
            label += this.$moment(event.start_date).format("M/D/YYYY");
            label += " - " + event.city + ", " + event.state;

            return label;
        }
    }
};
</script>